import React, { useState, useEffect } from 'react';
import { modelService, Model } from '../services/modelService';
import '../styles/dashboard-pages.css';

const ModelsPage: React.FC = () => {
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    setLoading(true);
    setError(null);
    try {
      const modelList = await modelService.listModels();
      console.log('[ModelsPage] Loaded models:', modelList);
      setModels(modelList);
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to load models';
      setError(errorMsg);
      console.error('[ModelsPage] Error loading models:', err);
      alert(`Error loading models: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const filteredModels = models.filter((model) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      model.name.toLowerCase().includes(query) ||
      (model.display_name && model.display_name.toLowerCase().includes(query)) ||
      (model.description && model.description.toLowerCase().includes(query))
    );
  });

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>Models</h1>
        <p>Manage and view your trained models</p>
      </div>

      <div className="page-actions">
        <button className="action-btn primary">
          + Create New Model
        </button>
        <button className="action-btn secondary">
          Upload Model
        </button>
      </div>

      <div className="page-sections">
        <section className="section-card">
          <div className="section-header">
            <h2>Your Models</h2>
            <input
              type="text"
              placeholder="Search models..."
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="empty-state">
              <div className="empty-icon">⏳</div>
              <p>Loading models...</p>
            </div>
          ) : error ? (
            <div className="empty-state">
              <div className="empty-icon">⚠️</div>
              <p>Error: {error}</p>
              <button className="action-btn secondary" onClick={loadModels} style={{ marginTop: '12px' }}>
                Retry
              </button>
            </div>
          ) : filteredModels.length > 0 ? (
            <div className="projects-list">
              {filteredModels.map((model) => (
                <div key={model.id} className="project-item">
                  <div className="project-info">
                    <div className="project-name">{model.display_name || model.name}</div>
                    <div className="project-description">{model.description || 'No description'}</div>
                    <div className="project-meta">
                      Type: {model.model_type || 'neural_network'} • Created: {formatDate(model.created_at)}
                    </div>
                  </div>
                  <div className="project-status">
                    <span className={`status-badge ${(model.training_status || 'not_trained').toLowerCase().replace('_', '-')}`}>
                      {model.training_status || 'not_trained'}
                    </span>
                  </div>
                  <div className="project-actions">
                    <button className="icon-btn" title="View">👁️</button>
                    <button className="icon-btn" title="Download">📥</button>
                    <button className="icon-btn danger" title="Delete">🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">🤖</div>
              <p>
                {searchQuery 
                  ? 'No models match your search.' 
                  : 'No models yet. Create or upload your first model to get started.'}
              </p>
            </div>
          )}
        </section>

        <section className="section-card">
          <h2>Model Templates</h2>
          <div className="template-grid">
            <div className="template-card">
              <div className="template-icon">🧠</div>
              <div className="template-name">Neural Network</div>
              <div className="template-desc">Basic feedforward network</div>
            </div>
            <div className="template-card">
              <div className="template-icon">🔄</div>
              <div className="template-name">Transformer</div>
              <div className="template-desc">Attention-based architecture</div>
            </div>
            <div className="template-card">
              <div className="template-icon">🖼️</div>
              <div className="template-name">CNN</div>
              <div className="template-desc">Convolutional neural network</div>
            </div>
            <div className="template-card">
              <div className="template-icon">📈</div>
              <div className="template-name">LSTM</div>
              <div className="template-desc">Recurrent neural network</div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ModelsPage;
